/**
 * Request durable (non-evictable) storage from the browser.
 * Safe to call multiple times — exits early if already granted.
 * Fire-and-forget: call without await when you don't need the result.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    const already = await navigator.storage.persisted();
    if (already) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/** Returns { usageMB, quotaMB, persisted } or null if the API is unavailable. */
export async function getStorageEstimate(): Promise<{
  usageMB: number;
  quotaMB: number;
  persisted: boolean;
} | null> {
  if (!navigator.storage?.estimate) return null;
  try {
    const [estimate, persisted] = await Promise.all([
      navigator.storage.estimate(),
      navigator.storage.persisted?.() ?? Promise.resolve(false),
    ]);
    const mb = (b: number | undefined) => Math.round((b ?? 0) / 1024 / 1024);
    return {
      usageMB: mb(estimate.usage),
      quotaMB: mb(estimate.quota),
      persisted,
    };
  } catch {
    return null;
  }
}
